// The sales tools listed on the staff page "Outils de vente" (/ventes). Everything is served by Syndiqia itself, behind
// the staff sign-in: documents from docs/sales/ shown as pages (/ventes/doc/<name>, served by apps/api/src/sales.ts),
// the call guide (/ventes/guide) and the cost comparator (/ventes/comparateur), and the decks exported to docs/presentations/exports/ (downloads).
// When a tool is added: list it here (and in apps/api/src/sales.ts for a document or a file), then deploy.

export type SalesTool = {
  title: string;
  description: string;
  /** A page of the app. */
  to?: string;
  /** Files to download, by name (apps/api/src/sales.ts SALES_FILES), with the label of each format. */
  files?: { name: string; label: string }[];
  /** Only platform admins see it (internal figures). */
  internal?: boolean;
};

export type SalesToolGroup = { title: string; tools: SalesTool[] };

/** Documents shown as pages: name → title (the API serves docs/sales/<file>). */
export const SALES_DOC_TITLES: Record<string, string> = {
  'trial-agreement': 'Entente d’essai gratuit (modèle)',
  cycle: 'Le cycle de vente (en anglais)',
  marketing: 'Marketing ciblé (en anglais)',
  objections: 'Objections (en anglais)',
  'prospect-sources': 'Sources de prospects (en anglais)',
};

/** Markdown file name → document name, to turn links between the documents into links between the pages. */
export const SALES_DOC_FILES: Record<string, string> = {
  'trial-agreement.md': 'trial-agreement',
  'cycle.md': 'cycle',
  'marketing.md': 'marketing',
  'objections.md': 'objections',
  'prospect-sources.md': 'prospect-sources',
};

export const SALES_TOOL_GROUPS: SalesToolGroup[] = [
  {
    title: 'Pour les appels et les rencontres',
    tools: [
      {
        title: 'Guide d’appel',
        description: 'Le premier contact : scripts par situation, arguments, échelle des offres, objections, courriels, grille de qualification, questions de découverte.',
        to: '/ventes/guide',
      },
      {
        title: 'Comparateur de coûts',
        description: 'Ce que l’association paie aujourd’hui, à côté des trois forfaits. À remplir avec le conseil pendant la découverte ou la démo.',
        to: '/ventes/comparateur',
      },
      {
        title: 'Script de la démo au conseil',
        description: 'Les 90 minutes de la démo : préparation, rôles, huit scènes dont deux en direct, questions, conclusion.',
        to: '/ventes/demo',
      },
      {
        title: 'Entente d’essai gratuit (modèle)',
        description: 'L’entente à signer avant la mise en service, avec les critères de succès et le calendrier des suivis. Non encore validée par un avocat.',
        to: '/ventes/doc/trial-agreement',
      },
    ],
  },
  {
    title: 'Présentations',
    tools: [
      {
        title: 'Présentation aux associations',
        description: 'Pour les conseils d’associations de propriétaires : ce que Syndiqia fait, et les automatisations en annexe.',
        files: [{ name: 'presentation-associations.pdf', label: 'PDF' }],
      },
      {
        title: 'Cycle de vente et marketing',
        description: 'Pour l’équipe : les étapes du premier contact à la conversion, et les canaux pour joindre les conseils.',
        files: [{ name: 'cycle-de-vente.pdf', label: 'PDF' }],
      },
      {
        title: 'Mise en service d’un nouveau client',
        description: 'Pour l’équipe qui fait les mises en service; la diapositive « Ce que le conseil prépare » peut être envoyée au client.',
        files: [{ name: 'mise-en-service.pdf', label: 'PDF' }],
      },
      {
        title: 'Carte des automatisations',
        description: 'Les 14 automatisations sur une carte : affiche 16:9 pour les présentations (3840 × 2160), affiche 4:5 pour LinkedIn (2400 × 3000), Le clip d’une minute (1080p, sans son) : Découvrir → « Voir la vidéo », ou app.syndiqia.com/video/carte-automatisations-clip.mp4. Version interactive : Découvrir → « Explorer la carte ».',
        files: [
          { name: 'carte-automatisations-paysage.png', label: '16:9 PNG' },
          { name: 'carte-automatisations-paysage.pdf', label: '16:9 PDF' },
          { name: 'carte-automatisations-portrait.png', label: '4:5 PNG' },
          { name: 'carte-automatisations-portrait.pdf', label: '4:5 PDF' },
        ],
      },
      {
        title: 'Modèle d’affaires',
        description: 'Prix, marché, projection et seuil de rentabilité. Interne : fondateurs et conseillers.',
        files: [{ name: 'modele-affaires.pdf', label: 'PDF' }],
        internal: true,
      },
    ],
  },
  {
    title: 'Pour aller plus loin',
    tools: [
      { title: 'Le cycle de vente', description: 'Chaque étape, du premier appel aux références (en anglais).', to: '/ventes/doc/cycle' },
      { title: 'Marketing ciblé', description: 'Les huit canaux pour joindre les conseils, et le premier trimestre suggéré (en anglais).', to: '/ventes/doc/marketing' },
      { title: 'Objections', description: 'Les objections fréquentes et les réponses (en anglais).', to: '/ventes/doc/objections' },
      { title: 'Sources de prospects', description: 'Le Registraire des entreprises et les autres sources, et les règles à respecter (en anglais).', to: '/ventes/doc/prospect-sources' },
    ],
  },
  {
    title: 'À montrer aux clients, dans l’application',
    tools: [
      { title: 'Découvrir Syndiqia', description: 'La présentation publique de la plateforme, à envoyer après un premier appel.', to: '/about' },
      { title: 'Manuel', description: 'Le guide d’utilisation complet, par tâche.', to: '/manual' },
    ],
  },
];
